<!--
  Paddings are a.o. optimized for the event detail page + series detailpage,
  hopefully they also work fine in the layout on other pages in e.g. content items.
  TODO: When you change them, make sure to check the event detail + series detail template in your browser as well.
  -->

<template>
  <article :id="newAnchor">
    <!-- Trigger -->
    <button
      class="flex w-full flex-row items-center justify-between gap-4 p-4 text-left sm:px-6 xl:px-8 cursor-pointer text-sm font-semibold"
      :class="{ 'bg-white': !colorTheme }"
      :aria-expanded="isOpen"
      :aria-controls="contentId"
      @click="handleClick"
    >
      <div class="flex min-w-0 flex-1 flex-row items-center gap-4">
        <time v-if="when" :datetime="when.toISOString()" class="w-20 shrink-0 text-app-primary-1-red--fire-brick sm:-ml-6 xl:-ml-8">{{ $d(when, 'short-date') }}</time>
        <h4
          v-if="label"
          class="min-w-0 flex-1 select-none"
          :class="{ 'text-white': colorTheme === 'dark-green' || colorTheme === 'petrol' || colorTheme === 'red' || colorTheme === 'violet' }"
        >
          {{ label }}
        </h4>
      </div>
      <AtomIconChevronDown
        class="h-4 w-4 shrink-0 transition duration-300 ease-in-out transform"
        :class="{
          'rotate-180': isOpen,
          'text-app-primary-1-red--torch': colorTheme !== 'dark-green' && colorTheme !== 'red' && colorTheme !== 'violet',
          'text-white': colorTheme === 'dark-green' || colorTheme === 'red' || colorTheme === 'violet',
        }"
      />
    </button>

    <!-- Foldout -->
    <SmoothReflow class="overflow-hidden transition-all duration-500 ease-in-out">
      <div
        v-if="isOpen"
        :id="contentId"
        role="region"
        :aria-labelledby="newAnchor"
        :class="{
          'h-auto': isOpen,
          'h-0': !isOpen,
          'border-app-primary-1-red--torch': colorTheme !== 'violet',
          'border-app-secondary-7-violet--purple-heart': colorTheme === 'violet',
        }"
        class="bg-app-primary-grey--black-squeeze p-4 sm:px-6 xl:px-8 py-6 border-l md:border-l-2 border-solid bg-white"
      >
        <div class="text-sm prose prose--app" :class="'prose--app--' + colorTheme" v-html="description" />
        <slot name="foldoutContent" />
      </div>
    </SmoothReflow>
  </article>
</template>

<script setup lang="ts">
const props = defineProps({
  label: { type: String, required: false, default: undefined },
  when: { type: Date, required: false, default: undefined },
  description: { type: String, required: false, default: undefined },
  colorTheme: { type: String, required: false, default: undefined },
  open: { type: Boolean, required: false, default: false },
  anchor: { type: String, required: false, default: undefined },
})

const isOpen = ref(props.open)

/*
 * `useId()` yields the same value on the server and on the client for a given component instance, unlike
 * `Math.random()`, which produced a different id on each side and so broke hydration on every item that
 * had no `anchor` from the CMS.
 */
const generatedAnchor = useId()
const newAnchor = ref(props.anchor === undefined ? generatedAnchor : props.anchor)

// Always suffixed, so the panel never shares its id with the <article> when the anchor comes from the CMS.
const contentId = computed(() => `${newAnchor.value}-foldout-content`)

function handleClick() {
  isOpen.value = !isOpen.value
  if (props.anchor) {
    history.replaceState(null, '', `#${props.anchor}`)
  }
}
onMounted(() => {
  if (props.anchor && window.location.hash === `#${props.anchor}`) {
    isOpen.value = true
  }
})
</script>
